"use client"

import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { styled } from "@mui/material/styles"
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  IconButton,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Collapse,
  Menu,
  MenuItem,
  Tooltip,
  Avatar,
  Checkbox,
  ListItemText,
  Chip,
  Badge,
} from "@mui/material"
import {
  Search as SearchIcon,
  Flag as FlagIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugIcon,
  Task as TaskIcon,
  Book as StoryIcon,
  Flag as EpicIcon,
  Assignment as TaskAssignmentIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  PlayArrow as SprintIcon,
} from "@mui/icons-material"
import axios from "axios"
import { getIssueTypeName } from './issuetype.jsx'
import EnhancedAISprintSuggestion from './AISprint.jsx'
import SprintSection from "./SprintSection"
import StartSprintDialog from "./StartSprint"
import BacklogIssue from "./BacklogIssue"
import TaskDetailsPanel from "./taskdetailpanel.jsx"

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000'

// Styled components
const BacklogContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(2),
  transition: "margin-right 0.3s ease-in-out",
}))

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}))

const FilterButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  marginRight: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}))

const CreateButton = styled(Button)(({ theme }) => ({
  color: "white",
  backgroundColor: "#0052CC",
  "&:hover": {
    backgroundColor: "#0747A6",
  },
  textTransform: 'none',
  fontWeight: 500,
}))

// Define issue types with proper Material-UI icon styling
const issueTypes = [
  {
    id: 1,
    name: "Bug",
    icon: <BugIcon style={{ color: "#ff8a50" }} />,
    color: "#ff8a50",
    description: "Something that is broken and needs to be fixed",
  },
  {
    id: 2,
    name: "Epic",
    icon: <EpicIcon style={{ color: "#9c27b0" }} />,
    color: "#9c27b0",
    description: "A large body of work that can be broken down into stories",
  },
  {
    id: 3,
    name: "Story",
    icon: <StoryIcon style={{ color: "#2196f3" }} />,
    color: "#2196f3",
    description: "A user story or feature requirement",
  },
  {
    id: 4,
    name: "Task",
    icon: <TaskIcon style={{ color: "#4caf50" }} />,
    color: "#4caf50",
    description: "A specific piece of work to be done",
  },
]

const priorities = [
  { id: 1, name: "Highest", icon: <FlagIcon fontSize="small" style={{ color: "#FF5630" }} /> },
  { id: 2, name: "High", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
  { id: 3, name: "Medium", icon: <FlagIcon fontSize="small" style={{ color: "#FFAB00" }} /> },
  { id: 4, name: "Low", icon: <FlagIcon fontSize="small" style={{ color: "#36B37E" }} /> },
  { id: 5, name: "Lowest", icon: <FlagIcon fontSize="small" style={{ color: "#57D9A3" }} /> },
  { id: 6, name: "Normal", icon: <FlagIcon fontSize="small" style={{ color: "#FF5630" }} /> },
  { id: 7, name: "P1", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
  { id: 8, name: "P2", icon: <FlagIcon fontSize="small" style={{ color: "#FFAB00" }} /> },
  { id: 9, name: "P3", icon: <FlagIcon fontSize="small" style={{ color: "#36B37E" }} /> },
  { id: 10, name: "P4", icon: <FlagIcon fontSize="small" style={{ color: "#57D9A3" }} /> },
  { id: 11, name: "P5", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
]

const statuses = [
  { id: 1, name: "TO DO", color: "#ede54e", textColor: "#172B4D" },
  { id: 2, name: "IN PROGRESS", color: "#1972f7", textColor: "#FFFFFF" },
  { id: 3, name: "DONE", color: "#049c02", textColor: "#FFFFFF" },
  { id: 4, name: "ON HOLD", color: "#FFAB00", textColor: "#172B4D" },
  { id: 5, name: "ISSUE CLOSED", color: "#00C7BE", textColor: "#172B4D" },
  { id: 12, name: "BACKLOG", color: "#FF8B00", textColor: "#FFFFFF" },
  { id: 13, name: "PLANNING", color: "#4C9AFF", textColor: "#FFFFFF" },
  { id: 14, name: "NEW", color: "#4C9AFF", textColor: "#FFFFFF" },
]

const BacklogPage = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()

  // State management
  const [startSprintDialogOpen, setStartSprintDialogOpen] = useState(false)
  const [selectedSprint, setSelectedSprint] = useState(null)
  const [editSprintDialogOpen, setEditSprintDialogOpen] = useState(false)
  const [sprintToEdit, setSprintToEdit] = useState(null)

  const [issues, setIssues] = useState([])
  const [projectDetails, setProjectDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [filteredIssues, setFilteredIssues] = useState([])
  const [activeFilter, setActiveFilter] = useState("all")
  const [anchorEl, setAnchorEl] = useState(null)
  const [toast, setToast] = useState({ open: false, message: "", type: "info" })
  const [draggingIssue, setDraggingIssue] = useState(null)
  const [sprints, setSprints] = useState([])
  const [backlogExpanded, setBacklogExpanded] = useState(true)
  const [nextSprintNumber, setNextSprintNumber] = useState(1)

  // Task Details Panel State
  const [selectedIssue, setSelectedIssue] = useState(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  // Dependencies state
  const [dependencies, setDependencies] = useState([])
  const [issueDependencies, setIssueDependencies] = useState(new Map())
  const [hasActiveSearch, setHasActiveSearch] = useState(false)
  const [aiSuggestionIssues, setAiSuggestionIssues] = useState([])
  
  // Filter states
  const [activeFilters, setActiveFilters] = useState({
    epic: false,
    story: false,
    task: false,
    bug: false
  })
  
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [selectedAssignees, setSelectedAssignees] = useState([])
  const [selectedPriorities, setSelectedPriorities] = useState([])
  const [selectedSprints, setSelectedSprints] = useState([]) // New sprint filter
  const [teamMembers, setTeamMembers] = useState([])
  
  const open = Boolean(anchorEl)

  // Filter menu states
  const [typeFilterAnchorEl, setTypeFilterAnchorEl] = useState(null)
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null)
  const [assigneeFilterAnchorEl, setAssigneeFilterAnchorEl] = useState(null)
  const [priorityFilterAnchorEl, setPriorityFilterAnchorEl] = useState(null)
  const [sprintFilterAnchorEl, setSprintFilterAnchorEl] = useState(null) // New sprint filter menu
  
  const typeFilterOpen = Boolean(typeFilterAnchorEl)
  const statusFilterOpen = Boolean(statusFilterAnchorEl)
  const assigneeFilterOpen = Boolean(assigneeFilterAnchorEl)
  const priorityFilterOpen = Boolean(priorityFilterAnchorEl)
  const sprintFilterOpen = Boolean(sprintFilterAnchorEl) // New sprint filter state

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState(null)
  const exportMenuOpen = Boolean(exportAnchorEl)
    
  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token')
    if (!token) {
      //console.error('No authentication token found')
      return null
    } 
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.company_id
    } catch (error) {
      //console.error('Error parsing token:', error)
      return null
    }
  }

  // Helper function to get status name
  const getStatusName = (status) => {
    if (!status) return '';
    
    // If status is already a string, return it
    if (typeof status === 'string') return status;
    
    // If status is an object with name property
    if (typeof status === 'object' && status.name) return status.name;
    
    // Try to find in statuses array
    const statusObj = statuses.find(s => s.id === status || s.name === status);
    return statusObj ? statusObj.name : String(status);
  }

  // Helper function to get priority name
  const getPriorityName = (priority) => {
    if (!priority) return '';
    
    // If priority is already a string, return it
    if (typeof priority === 'string') return priority;
    
    // If priority is an object with name property
    if (typeof priority === 'object' && priority.name) return priority.name;
    
    // Try to find in priorities array
    const priorityObj = priorities.find(p => p.id === priority || p.name === priority);
    return priorityObj ? priorityObj.name : String(priority);
  }

  // Get sprint name by issue id
  const getSprintNameForIssue = (issueId) => {
    for (const sprint of sprints) {
      if (sprint.issues.some(issue => issue.id === issueId)) {
        return sprint.name
      }
    }
    return "Backlog"
  }

  // Fetch team members for assignee filter
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!projectId) return

      try {
        const token = localStorage.getItem('token')
        if (!token) {
          alert("No authentication token found. Please log in.")
          return
        }
  
        const payload = JSON.parse(atob(token.split('.')[1]))
        const company_id = payload.company_id
        
        const response = await fetch(`${API_URL}/api/addteam/project/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        const data = await response.json()
        
        let allMembers = []
        
        if (data.teamMembers) {
          if (Array.isArray(data.teamMembers)) {
            if (data.teamMembers.length > 0 && Array.isArray(data.teamMembers[0])) {
              allMembers = data.teamMembers.flat()
            } else {
              allMembers = data.teamMembers
            }
          }
          
          if (data.developers && Array.isArray(data.developers)) {
            allMembers = [...allMembers, ...data.developers]
          }
          if (data.testers && Array.isArray(data.testers)) {
            allMembers = [...allMembers, ...data.testers]
          }
          if (data.managers && Array.isArray(data.managers)) {
            allMembers = [...allMembers, ...data.managers]
          }
          
          // Get unique members by employee_id
          const uniqueMembers = []
          const seenIds = new Set()
          
          allMembers.forEach(member => {
            const memberId = member.employee_id || member.id
            const memberName = member.name || member.username || member.email || `Member ${memberId}`
            
            if (!seenIds.has(memberId)) {
              seenIds.add(memberId)
              uniqueMembers.push({
                id: memberId,
                name: memberName,
                email: member.email,
                role: member.role
              })
            }
          })
          
          setTeamMembers(uniqueMembers)
        }
      } catch (error) {
        //console.error("Error fetching team members:", error)
        setTeamMembers([])
      }
    }

    fetchTeamMembers()
  }, [projectId])

  // Get all unique assignees from issues
  const getAllAssignees = () => {
    const assigneeSet = new Set()
    
    issues.forEach(issue => {
      if (issue.assigneeName || issue.assignee) {
        assigneeSet.add(issue.assigneeName || issue.assignee)
      }
    })
    
    return Array.from(assigneeSet).sort()
  }

  // Filter issues based on all active filters
  useEffect(() => {
    if (!issues.length) return
    
    let filtered = [...issues]
    
    // Search filter
    const hasSearch = searchQuery.trim() !== ''
    setHasActiveSearch(hasSearch)
    
    if (hasSearch) {
      filtered = filtered.filter(issue => 
        issue.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (issue.assignee && issue.assignee.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }
    
    // Type filter
    const activeTypeFilters = Object.keys(activeFilters).filter(key => activeFilters[key])
    if (activeTypeFilters.length > 0) {
      filtered = filtered.filter(issue => {
        const issueTypeName = getIssueTypeName(issue.issueType || issue.type)
        return activeTypeFilters.includes(issueTypeName.toLowerCase())
      })
    }
    
    // Status filter - FIXED
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(issue => {
        const issueStatus = getStatusName(issue.status || '')
        return selectedStatuses.includes(issueStatus)
      })
    }
    
    // Assignee filter
    if (selectedAssignees.length > 0) {
      filtered = filtered.filter(issue => {
        const assigneeName = issue.assigneeName || issue.assignee || 'Unassigned'
        return selectedAssignees.includes(assigneeName)
      })
    }
    
    // Priority filter
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter(issue => {
        const priorityName = getPriorityName(issue.priority || '')
        return selectedPriorities.includes(priorityName)
      })
    }
    
    // Sprint filter - NEW
    if (selectedSprints.length > 0) {
      filtered = filtered.filter(issue => {
        const issueSprintName = getSprintNameForIssue(issue.id)
        return selectedSprints.includes(issueSprintName)
      })
    }
    
    setFilteredIssues(filtered)
  }, [
    searchQuery, 
    issues, 
    activeFilters, 
    selectedStatuses, 
    selectedAssignees, 
    selectedPriorities, 
    selectedSprints,
    sprints
  ])

  // Get all visible issues (what user sees on screen) - Updated for sprint filter
  const getVisibleIssues = () => {
    let allIssues = []
    
    // Get issues from backlog if selected
    if (selectedSprints.length === 0 || selectedSprints.includes("Backlog")) {
      const backlogIssues = filteredIssues.filter(issue => 
        !sprints.some(sprint => sprint.issues.some(sprintIssue => sprintIssue.id === issue.id))
      )
      allIssues.push(...backlogIssues)
    }
    
    // Get issues from selected sprints
    sprints.forEach(sprint => {
      if (selectedSprints.length === 0 || selectedSprints.includes(sprint.name)) {
        if (sprint.expanded) {
          sprint.issues.forEach(issue => {
            // Check if issue matches current filters
            const matchesSearch = !searchQuery.trim() || 
              issue.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
              issue.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (issue.assignee && issue.assignee.toLowerCase().includes(searchQuery.toLowerCase()))

            const activeTypeFilters = Object.keys(activeFilters).filter(key => activeFilters[key])
            let matchesTypeFilter = true
            
            if (activeTypeFilters.length > 0) {
              const issueTypeName = getIssueTypeName(issue.issueType || issue.type)
              matchesTypeFilter = activeTypeFilters.includes(issueTypeName.toLowerCase())
            }

            let matchesStatusFilter = true
            if (selectedStatuses.length > 0) {
              const statusName = getStatusName(issue.status || '')
              matchesStatusFilter = selectedStatuses.includes(statusName)
            }

            let matchesAssigneeFilter = true
            if (selectedAssignees.length > 0) {
              const assigneeName = issue.assigneeName || issue.assignee || 'Unassigned'
              matchesAssigneeFilter = selectedAssignees.includes(assigneeName)
            }

            let matchesPriorityFilter = true
            if (selectedPriorities.length > 0) {
              const priorityName = getPriorityName(issue.priority || '')
              matchesPriorityFilter = selectedPriorities.includes(priorityName)
            }

            if (matchesSearch && matchesTypeFilter && matchesStatusFilter && matchesAssigneeFilter && matchesPriorityFilter) {
              allIssues.push(issue)
            }
          })
        }
      }
    })

    return allIssues
  }

  // Export functionality - Updated to respect sprint filter and use helper functions
  const handleExport = async () => {
    try {
      setLoading(true)
      
      // Get all visible issues
      const visibleIssues = getVisibleIssues()
      
      if (visibleIssues.length === 0) {
        setToast({
          open: true,
          message: "No issues to export",
          type: "warning",
        })
        return
      }

      // Prepare CSV content
      const headers = [
        'Issue Key',
        'Summary',
        'Issue Type',
        'Status',
        'Priority',
        'Assignee',
        'Description',
        'Sprint',
      ]

      const csvRows = visibleIssues.map(issue => {
        const sprintName = getSprintNameForIssue(issue.id)
        const statusName = getStatusName(issue.status)
        const priorityName = getPriorityName(issue.priority)

        return [
          issue.key || '',
          issue.summary || '',
          getIssueTypeName(issue.issueType || issue.type) || '',
          statusName,
          priorityName,
          issue.assigneeName || issue.assignee || 'Unassigned',
          issue.description || '',
          sprintName,
        ]
      })

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...csvRows.map(row => row.map(cell => 
          `"${String(cell).replace(/"/g, '""')}"`
        ).join(','))
      ].join('\n')

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', `${projectDetails?.key || 'project'}_backlog_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setToast({
        open: true,
        message: `Exported ${visibleIssues.length} issue(s) to CSV`,
        type: "success",
      })

    } catch (error) {
      //console.error("Error exporting issues:", error)
      setToast({
        open: true,
        message: "Failed to export issues",
        type: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  // Export as JSON (optional alternative)
  const handleExportJSON = async () => {
    try {
      setLoading(true)
      
      const visibleIssues = getVisibleIssues()
      
      if (visibleIssues.length === 0) {
        setToast({
          open: true,
          message: "No issues to export",
          type: "warning",
        })
        return
      }

      const exportData = visibleIssues.map(issue => {
        const sprint = sprints.find(s => 
          s.issues.some(sprintIssue => sprintIssue.id === issue.id)
        )

        return {
          issueKey: issue.key,
          summary: issue.summary,
          issueType: getIssueTypeName(issue.issueType || issue.type),
          status: getStatusName(issue.status),
          priority: getPriorityName(issue.priority),
          assignee: issue.assigneeName || issue.assignee || 'Unassigned',
          createdDate: issue.createdAt,
          updatedDate: issue.updatedAt,
          storyPoints: issue.storyPoints,
          description: issue.description,
          parentIssue: issue.parent,
          sprint: getSprintNameForIssue(issue.id),
          sprintId: sprint?.id || null,
          labels: issue.labels || [],
          dependencies: issueDependencies.get(issue.id) || {}
        }
      })

      const jsonString = JSON.stringify(exportData, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', `${projectDetails?.key || 'project'}_backlog_${new Date().toISOString().split('T')[0]}.json`)
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setToast({
        open: true,
        message: `Exported ${visibleIssues.length} issue(s) to JSON`,
        type: "success",
      })

    } catch (error) {
      //console.error("Error exporting issues as JSON:", error)
      setToast({
        open: true,
        message: "Failed to export issues",
        type: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  // Export menu handlers
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setExportAnchorEl(null)
  }

  const handleExportMenuItemClick = (format) => {
    handleExportClose()
    if (format === 'csv') {
      handleExport()
    } else if (format === 'json') {
      handleExportJSON()
    }
  }

  // Filter handlers
  const handleTypeFilterClick = (event) => {
    setTypeFilterAnchorEl(event.currentTarget)
  }

  const handleTypeFilterClose = () => {
    setTypeFilterAnchorEl(null)
  }

  const handleStatusFilterClick = (event) => {
    setStatusFilterAnchorEl(event.currentTarget)
  }

  const handleStatusFilterClose = () => {
    setStatusFilterAnchorEl(null)
  }

  const handleAssigneeFilterClick = (event) => {
    setAssigneeFilterAnchorEl(event.currentTarget)
  }

  const handleAssigneeFilterClose = () => {
    setAssigneeFilterAnchorEl(null)
  }

  const handlePriorityFilterClick = (event) => {
    setPriorityFilterAnchorEl(event.currentTarget)
  }

  const handlePriorityFilterClose = () => {
    setPriorityFilterAnchorEl(null)
  }

  // New sprint filter handler
  const handleSprintFilterClick = (event) => {
    setSprintFilterAnchorEl(event.currentTarget)
  }

  const handleSprintFilterClose = () => {
    setSprintFilterAnchorEl(null)
  }

  // Handle filter selections
  const handleTypeFilterToggle = (filterType) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }))
  }

  const handleStatusFilterToggle = (statusName) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusName)) {
        return prev.filter(s => s !== statusName)
      } else {
        return [...prev, statusName]
      }
    })
  }

  const handleAssigneeFilterToggle = (assigneeName) => {
    setSelectedAssignees(prev => {
      if (prev.includes(assigneeName)) {
        return prev.filter(a => a !== assigneeName)
      } else {
        return [...prev, assigneeName]
      }
    })
  }

  const handlePriorityFilterToggle = (priorityName) => {
    setSelectedPriorities(prev => {
      if (prev.includes(priorityName)) {
        return prev.filter(p => p !== priorityName)
      } else {
        return [...prev, priorityName]
      }
    })
  }

  // New sprint filter toggle
  const handleSprintFilterToggle = (sprintName) => {
    setSelectedSprints(prev => {
      if (prev.includes(sprintName)) {
        return prev.filter(s => s !== sprintName)
      } else {
        return [...prev, sprintName]
      }
    })
  }

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters({
      epic: false,
      story: false,
      task: false,
      bug: false
    })
    setSelectedStatuses([])
    setSelectedAssignees([])
    setSelectedPriorities([])
    setSelectedSprints([])
    setSearchQuery('')
    handleTypeFilterClose()
    handleStatusFilterClose()
    handleAssigneeFilterClose()
    handlePriorityFilterClose()
    handleSprintFilterClose()
  }

  // Check if any filter is active
  const hasAnyFilter = () => {
    return Object.values(activeFilters).some(Boolean) ||
           selectedStatuses.length > 0 ||
           selectedAssignees.length > 0 ||
           selectedPriorities.length > 0 ||
           selectedSprints.length > 0 ||
           searchQuery.trim() !== ''
  }

  // Get filter display text
  const getFilterButtonText = (type) => {
    switch(type) {
      case 'type':
        const activeTypes = Object.keys(activeFilters).filter(key => activeFilters[key])
        return activeTypes.length > 0 ? `Type (${activeTypes.length})` : 'Type'
      case 'status':
        return selectedStatuses.length > 0 ? `Status (${selectedStatuses.length})` : 'Status'
      case 'assignee':
        return selectedAssignees.length > 0 ? `Assignee (${selectedAssignees.length})` : 'Assignee'
      case 'priority':
        return selectedPriorities.length > 0 ? `Priority (${selectedPriorities.length})` : 'Priority'
      case 'sprint':
        return selectedSprints.length > 0 ? `Sprint (${selectedSprints.length})` : 'Sprint'
      default:
        return ''
    }
  }

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name || name === "Unassigned") return "UA"
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  const backlogIssuesForAI = filteredIssues.filter(issue => 
    !sprints.some(sprint => sprint.issues.some(sprintIssue => sprintIssue.id === issue.id))
  )

  const handleApplyAISuggestion = (issueIds, sprintConfig) => {
    handleCreateSprintWithIssues(issueIds, sprintConfig)
  }

  const handleCreateSprintWithIssues = async (issueIds, sprintConfig) => {
    const company_id = getCompanyIdFromToken()
    if (!company_id) {
      setToast({
        open: true,
        message: "Authentication error. Please log in again.",
        type: "error",
      })
      return
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/sprints`,
        {
          name: sprintConfig.name,
          goal: sprintConfig.goal,
          duration: sprintConfig.duration * 7,
          status: "FUTURE",
          projectKey: projectDetails?.key,

        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (response.data && response.data.data) {
        const newSprint = {
          ...response.data.data,
          name: sprintConfig.name,
          goal: sprintConfig.goal,
          issues: [],
          expanded: true,
        }

        for (const issueId of issueIds) {
          const issue = issues.find(issue => issue.id === issueId)
          if (issue) {
            await axios.post(`${API_URL}/api/sprints/add-issue`, {
              issueKey: issue.key,
              sprintId: newSprint.id,
            },{
              headers: {
                Authorization: `Bearer ${token}`
              }
            })
            newSprint.issues.push(issue)
          }
        }

        setSprints((prevSprints) => [...prevSprints, newSprint])
        setNextSprintNumber(nextSprintNumber + 1)

        setToast({
          open: true,
          message: `${sprintConfig.name} created with ${issueIds.length} AI-suggested issues`,
          type: "success",
        })
      }
    } catch (err) {
      //console.error("Error creating sprint with AI suggestions:", err)
      setToast({
        open: true,
        message: "Failed to create sprint with suggested issues",
        type: "error",
      })
    }
  }

  const fetchDependencies = async () => {
    try {
      const allDependencies = []
      const dependencyMap = new Map()

      for (const issue of issues) {
        try {
          const token = localStorage.getItem('token')
          if (!token) {
            alert("No authentication token found. Please log in.")
            return
          }
  
          const payload = JSON.parse(atob(token.split('.')[1]))
          const company_id = payload.company_id
          const response = await axios.get(`${API_URL}/api/issues/${issue.id}/dependencies`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          )

          if (response.data.data && response.data.data.length > 0) {
            const transformedDependencies = response.data.data
              .map((dep) => {
                if (!dep.sourceIssue || !dep.targetIssue) {
                  return null
                }

                return {
                  id: dep.dependencyId || `${dep.sourceIssue.id}-${dep.targetIssue.id}`,
                  sourceIssueId: dep.sourceIssue.id,
                  targetIssueId: dep.targetIssue.id,
                  linkType: dep.linkType || dep.dependency_type,
                  direction: dep.direction,
                  sourceIssue: dep.sourceIssue,
                  targetIssue: dep.targetIssue,
                }
              })
              .filter((dep) => dep !== null)

            allDependencies.push(...transformedDependencies)

            transformedDependencies.forEach((dep) => {
              const linkType = dep.linkType.toLowerCase()
              
              if (!dependencyMap.has(dep.sourceIssueId)) {
                dependencyMap.set(dep.sourceIssueId, { blocks: [], blockedBy: [], relatesTo: [] })
              }
              if (!dependencyMap.has(dep.targetIssueId)) {
                dependencyMap.set(dep.targetIssueId, { blocks: [], blockedBy: [], relatesTo: [] })
              }

              if (linkType === 'blocks') {
                dependencyMap.get(dep.sourceIssueId).blocks.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blockedBy.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === 'is blocked by') {
                dependencyMap.get(dep.sourceIssueId).blockedBy.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blocks.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === 'relates to') {
                dependencyMap.get(dep.sourceIssueId).relatesTo.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).relatesTo.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === 'duplicates') {
                dependencyMap.get(dep.sourceIssueId).blocks.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blockedBy.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              } else if (linkType === 'is duplicated by') {
                dependencyMap.get(dep.sourceIssueId).blockedBy.push({
                  issue: dep.targetIssue,
                  linkType: dep.linkType,
                })
                dependencyMap.get(dep.targetIssueId).blocks.push({
                  issue: dep.sourceIssue,
                  linkType: dep.linkType,
                })
              }
            })
          }
        } catch (err) {
        }
      }

      setDependencies(allDependencies)
      setIssueDependencies(dependencyMap)
    } catch (err) {
      //console.error("Error fetching dependencies:", err)
    }
  }

  const getIssueDependencyInfo = (issueId) => {
    return issueDependencies.get(issueId) || { blocks: [], blockedBy: [], relatesTo: [] }
  }

  // Fetch project details
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!projectId) return

      try {
        const token = localStorage.getItem('token')
        if (!token) {
          alert("No authentication token found. Please log in.")
          return
        }
  
        const payload = JSON.parse(atob(token.split('.')[1]))
        const company_id = payload.company_id
        const response = await axios.get(`${API_URL}/api/projects/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        if (response.data && response.data.status === "success") {
          setProjectDetails(response.data.data)

          try {
            const projectData = JSON.parse(localStorage.getItem("projectData") || "{}")
            projectData[projectId] = {
              id: projectId,
              name: response.data.data.name,
              key: response.data.data.key,
            }
            localStorage.setItem("projectData", JSON.stringify(projectData))
          } catch (err) {
            //console.error("Error updating project data in localStorage:", err)
          }
        }
      } catch (err) {
        //console.error("Error fetching project details:", err)
        setError("Failed to load project details")
      }
    }

    fetchProjectDetails()
  }, [projectId])

  // Fetch issues
  useEffect(() => {
    const fetchIssues = async () => {
      if (!projectId) return

      try {
        const token = localStorage.getItem('token')
        if (!token) {
          alert("No authentication token found. Please log in.")
          return
        }
  
        const payload = JSON.parse(atob(token.split('.')[1]))
        const company_id = payload.company_id
        setLoading(true)
        const response = await axios.get(`${API_URL}/api/issues?project=${projectId}`,{
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        if (response.data && response.data.data) {
          setIssues(response.data.data)
        } else {
          setIssues([])
        }
        setLoading(false)
      } catch (err) {
        //console.error("Error fetching issues:", err)
        setError(err.message || "Failed to fetch issues")
        setLoading(false)
      }
    }

    fetchIssues()
  }, [projectId])

  // Fetch sprints
  useEffect(() => {
    const fetchSprints = async () => {
      if (!projectDetails?.key) return
      const token = localStorage.getItem('token');
      const company_id = getCompanyIdFromToken()
      if (!company_id) {
        //console.error('Cannot fetch sprints: No company_id found')
        return
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/sprints/project/${projectDetails.key}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        
        if (response.data && response.data.data) {
          const sprintsWithExpanded = response.data.data.map((sprint, index) => ({
            ...sprint,
            expanded: true,
            issues: [],
            name: sprint.name || `${projectDetails.key} Sprint ${index + 1}`,
          }))

          setSprints(sprintsWithExpanded)

          const maxSprintNumber = sprintsWithExpanded.reduce((max, sprint) => {
            const sprintNumberMatch = sprint.name.match(/Sprint (\d+)/)
            const sprintNumber = sprintNumberMatch ? Number.parseInt(sprintNumberMatch[1], 10) : 0
            return Math.max(max, sprintNumber)
          }, 0)

          setNextSprintNumber(maxSprintNumber + 1)

          sprintsWithExpanded.forEach(async (sprint) => {
            try {
              const token = localStorage.getItem('token');
              const issuesResponse = await axios.get(
                `${API_URL}/api/sprints/${sprint.id}/issues`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`
                  }
                }
              )
              if (issuesResponse.data && issuesResponse.data.data) {
                setSprints((prevSprints) =>
                  prevSprints.map((s) => (s.id === sprint.id ? { ...s, issues: issuesResponse.data.data } : s)),
                )
              }
            } catch (err) {
              //console.error(`Error fetching issues for sprint ${sprint.id}:`, err)
            }
          })
        }
      } catch (err) {
        //console.error("Error fetching sprints:", err)
      }
    }

    fetchSprints()
  }, [projectDetails])

  const handleCreateSprint = async () => {
    const sprintName = `${projectDetails?.key || "Project"} Sprint ${nextSprintNumber}`
    const token = localStorage.getItem('token');
    const company_id = getCompanyIdFromToken()
    if (!company_id) {
      setToast({
        open: true,
        message: "Authentication error. Please log in again.",
        type: "error",
      })
      return
    }
  
    try {
      const response = await axios.post(
        `${API_URL}/api/sprints`,
        {
          name: sprintName,
          status: "FUTURE",
          projectKey: projectDetails?.key,
        },{
          headers: {
               "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      )
  
      if (response.data && response.data.data) {
        const newSprint = {
          ...response.data.data,
          name: sprintName,
          issues: [],
          expanded: true,
        }
  
        setSprints((prevSprints) => [...prevSprints, newSprint])
        setNextSprintNumber(nextSprintNumber + 1)
  
        setToast({
          open: true,
          message: `${sprintName} created`,
          type: "success",
        })
      }
    } catch (err) {
      //console.error("Error creating sprint:", err)
      setToast({
        open: true,
        message: "Failed to create sprint",
        type: "error",
      })
    }
  }

  const handleOpenStartSprintDialog = (sprint) => {
    setSelectedSprint(sprint)
    setStartSprintDialogOpen(true)
  }

  const handleCloseStartSprintDialog = () => {
    setSelectedSprint(null)
    setStartSprintDialogOpen(false)
  }

  const handleIssueDelete = (deletedIssueId) => {
    setIssues((prevIssues) => prevIssues.filter((issue) => issue.id !== deletedIssueId))

    setSprints((prevSprints) =>
      prevSprints.map((sprint) => ({
        ...sprint,
        issues: sprint.issues.filter((issue) => issue.id !== deletedIssueId),
      })),
    )

    setFilteredIssues((prevFilteredIssues) => prevFilteredIssues.filter((issue) => issue.id !== deletedIssueId))

    if (selectedIssue && selectedIssue.id === deletedIssueId) {
      setIsPanelOpen(false)
      setSelectedIssue(null)
    }

    setToast({
      open: true,
      message: "Issue deleted successfully",
      type: "success",
    })
  }

  const handleSprintStarted = (updatedSprint) => {
    setSprints(sprints.map((sprint) => (sprint.id === updatedSprint.id ? { ...sprint, ...updatedSprint } : sprint)))

    setToast({
      open: true,
      message: `${updatedSprint.name} started`,
      type: "success",
    })

    handleCloseStartSprintDialog()
  }

  const goToCreateIssuePage = () => {
    navigate(`/project/${projectId}/create-task`)
  }

  const handleEditSprint = (sprint) => {
    setSprintToEdit(sprint)
    setEditSprintDialogOpen(true)
  }

  const handleCloseEditSprintDialog = () => {
    setSprintToEdit(null)
    setEditSprintDialogOpen(false)
  }

  const handleSprintUpdated = (updatedSprint) => {
    setSprints(sprints.map((sprint) => (sprint.id === updatedSprint.id ? { ...sprint, ...updatedSprint } : sprint)))

    setToast({
      open: true,
      message: `${updatedSprint.name} updated successfully`,
      type: "success",
    })

    handleCloseEditSprintDialog()
  }

  const handleSprintDeleted = (sprintId) => {
    setSprints(sprints.filter((sprint) => sprint.id !== sprintId))

    setToast({
      open: true,
      message: "Sprint deleted successfully",
      type: "success",
    })
  }

  const handleToastClose = () => {
    setToast({ ...toast, open: false })
  }

  const handleDragStart = (issueId) => {
    setDraggingIssue(issueId)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDropOnBacklog = (e) => {
    e.preventDefault()
    if (draggingIssue) {
      let sprintId = null

      sprints.forEach((sprint) => {
        const issueIndex = sprint.issues.findIndex((issue) => issue.id === draggingIssue)
        if (issueIndex !== -1) {
          sprintId = sprint.id
        }
      })

      if (sprintId) {
        removeIssueFromSprint(draggingIssue, sprintId)
      }

      setToast({
        open: true,
        message: "Issue moved to Backlog",
        type: "success",
      })
      setDraggingIssue(null)
    }
  }

  const removeIssueFromSprint = async (issueId, sprintId) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        alert("No authentication token found. Please log in.")
        return
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]))
      const company_id = payload.company_id
      const issue = issues.find((issue) => issue.id === issueId)
      if (!issue) return

      await axios.delete(`${API_URL}/api/sprints/remove-issue/${issue.key}`,
        {
          headers: {
            
            Authorization: `Bearer ${token}`
          }
        }
      )

      setSprints((prevSprints) =>
        prevSprints.map((sprint) => {
          if (sprint.id === sprintId) {
            return {
              ...sprint,
              issues: sprint.issues.filter((issue) => issue.id !== issueId),
            }
          }
          return sprint
        }),
      )
    } catch (err) {
      //console.error("Error removing issue from sprint:", err)
      setToast({
        open: true,
        message: "Failed to remove issue from sprint",
        type: "error",
      })
    }
  }

  const handleDropOnSprint = async (e, sprintId) => {
    e.preventDefault()
    const token = localStorage.getItem('token')
    if (!token) {
      alert("No authentication token found. Please log in.")
      return
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]))
    const company_id = payload.company_id
    if (draggingIssue) {
      const draggedIssue = issues.find((issue) => issue.id === draggingIssue)
      if (!draggedIssue) return

      try {
        await axios.post(`${API_URL}/api/sprints/add-issue`, {
          issueKey: draggedIssue.key,
          sprintId: sprintId,
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      )

        sprints.forEach((sprint) => {
          if (sprint.id !== sprintId) {
            const issueInOtherSprint = sprint.issues.find((issue) => issue.id === draggingIssue)
            if (issueInOtherSprint) {
              removeIssueFromSprint(draggingIssue, sprint.id)
            }
          }
        })

        setSprints((prevSprints) =>
          prevSprints.map((sprint) => {
            if (sprint.id === sprintId) {
              const alreadyInSprint = sprint.issues.some((issue) => issue.id === draggingIssue)
              if (!alreadyInSprint) {
                return {
                  ...sprint,
                  issues: [...sprint.issues, draggedIssue],
                }
              }
            }
            return sprint
          }),
        )

        setToast({
          open: true,
          message: `Issue moved to ${sprints.find((s) => s.id === sprintId)?.name || "Sprint"}`,
          type: "success",
        })
      } catch (err) {
        //console.error("Error adding issue to sprint:", err)
        setToast({
          open: true,
          message: "Failed to add issue to sprint",
          type: "error",
        })
      }

      setDraggingIssue(null)
    }
  }

  const toggleSprintExpanded = (sprintId) => {
    setSprints(sprints.map((sprint) => (sprint.id === sprintId ? { ...sprint, expanded: !sprint.expanded } : sprint)))
  }

  const toggleBacklogExpanded = () => {
    setBacklogExpanded(!backlogExpanded)
  }

  const handleIssueClick = (issue) => {
    setSelectedIssue(issue)
    setIsPanelOpen(true)
  }

  const handlePanelClose = () => {
    setIsPanelOpen(false)
    setSelectedIssue(null)
  }

  const handleParentChange = (issueId, newParent) => {
    setIssues((prevIssues) =>
      prevIssues.map((issue) => (issue.id === issueId ? { ...issue, parent: newParent } : issue)),
    )

    setSprints((prevSprints) =>
      prevSprints.map((sprint) => ({
        ...sprint,
        issues: sprint.issues.map((issue) => (issue.id === issueId ? { ...issue, parent: newParent } : issue)),
      })),
    )

    setToast({
      open: true,
      message: "Parent updated successfully",
      type: "success",
    })
  }

  const handleMenuAction = (action, issue, data) => {
    switch (action) {
      case "delete":
        handleIssueDelete(issue.id)
        break
      case "move":
        break
      case "copy":
        break
      default:
    }
  }

  const handleMoveIssue = (issue, destination) => {
    const destName = typeof destination === 'object' && destination !== null ? (destination.name || 'Destination') : destination;
    setToast({
      open: true,
      message: `Issue ${issue.key} moved to ${destName}`,
      type: "success",
    })
  }

  const handleIssueUpdate = (updatedIssue) => {
    setIssues((prevIssues) => prevIssues.map((issue) => (issue.id === updatedIssue.id ? updatedIssue : issue)))

    setSprints((prevSprints) =>
      prevSprints.map((sprint) => ({
        ...sprint,
        issues: sprint.issues.map((issue) => (issue.id === updatedIssue.id ? updatedIssue : issue)),
      })),
    )

    setSelectedIssue(updatedIssue)

    setToast({
      open: true,
      message: "Issue updated successfully",
      type: "success",
    })
  }

  const handleAssigneeUpdate = (issueId, newAssignee) => {
    setIssues((prevIssues) =>
      prevIssues.map((issue) => (issue.id === issueId ? { ...issue, assignee: newAssignee } : issue)),
    )

    setSprints((prevSprints) =>
      prevSprints.map((sprint) => ({
        ...sprint,
        issues: sprint.issues.map((issue) => (issue.id === issueId ? { ...issue, assignee: newAssignee } : issue)),
      })),
    )

    setToast({
      open: true,
      message: "Assignee updated successfully",
      type: "success",
    })
  }

  const handleStatusUpdate = (issueId, newStatus) => {
    setIssues((prevIssues) =>
      prevIssues.map((issue) => (issue.id === issueId ? { ...issue, status: getStatusName(newStatus) } : issue)),
    )

    setSprints((prevSprints) =>
      prevSprints.map((sprint) => ({
        ...sprint,
        issues: sprint.issues.map((issue) =>
          issue.id === issueId ? { ...issue, status: getStatusName(newStatus) } : issue,
        ),
      })),
    )

    setToast({
      open: true,
      message: "Status updated successfully",
      type: "success",
    })
  }

  const handlePriorityUpdate = (issueId, newPriority) => {
    setIssues((prevIssues) =>
      prevIssues.map((issue) => (issue.id === issueId ? { ...issue, priority: getPriorityName(newPriority) } : issue)),
    )

    setSprints((prevSprints) =>
      prevSprints.map((sprint) => ({
        ...sprint,
        issues: sprint.issues.map((issue) =>
          issue.id === issueId ? { ...issue, priority: getPriorityName(newPriority) } : issue,
        ),
      })),
    )

    setToast({
      open: true,
      message: "Priority updated successfully",
      type: "success",
    })
  }

  const renderBacklogContent = () => {
    const backlogIssues = filteredIssues.filter(issue => 
      !sprints.some(sprint => sprint.issues.some(sprintIssue => sprintIssue.id === issue.id))
    )
    
    if (backlogIssues.length === 0) {
      return (
        <Box sx={{ 
          py: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          textAlign: 'center',
          px: 2
        }}>
          {hasAnyFilter() ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                There's nothing that matches this filter
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mb: 2 }}>
                Try adjusting your search or filter to find what you're looking for.
              </Typography>
              <Button
                variant="outlined"
                onClick={clearAllFilters}
                startIcon={<ClearIcon />}
              >
                Clear search and filters
              </Button>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                No issues found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mb: 2 }}>
                Create your first issue or adjust your search filters.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={goToCreateIssuePage}
              >
                Change Request
              </Button>
            </>
          )}
        </Box>
      )
    }

    return (
      <Box sx={{ p: 2 }}>
        <Grid container sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary", px: 2, py: 1 }}>
          <Grid item xs={4} sm={3} md={4} sx={{ textAlign: 'center' }}>SUMMARY</Grid>
          <Grid item xs={2} sm={1} md={1} sx={{ textAlign: 'center' }}>ISSUE TYPE</Grid>
          <Grid item xs={2} sm={2} md={2} sx={{ textAlign: "center" }}>KEY</Grid>
          <Grid item xs={2} sm={2} md={2} sx={{ textAlign: "center" }}>ASSIGNEE</Grid>
          <Grid item xs={1} sm={2} md={2} sx={{ textAlign: "center" }}>STATUS</Grid>
          <Grid item xs={1} sm={2} md={1} sx={{ textAlign: "center" }}>PRIORITY</Grid>
        </Grid>

        {backlogIssues.map((issue) => (
          <BacklogIssue
            key={issue.id}
            issue={issue}
            onDragStart={handleDragStart}
            onNavigate={() => handleIssueClick(issue)}
            projectId={projectId}
            issueTypes={issueTypes}
            statuses={statuses}
            priorities={priorities}
            getInitials={getInitials}
            onAssigneeChange={handleAssigneeUpdate}
            onStatusChange={handleStatusUpdate}
            onPriorityChange={handlePriorityUpdate}
            projectKey={projectDetails?.key}
            onDelete={handleIssueDelete}
            onParentChange={handleParentChange}
            onMenuAction={handleMenuAction}
            onMove={handleMoveIssue}
            onIssueUpdated={handleIssueUpdate}
            dependencyInfo={getIssueDependencyInfo(issue.id)}
          />
        ))}
      </Box>
    )
  }

  if (loading && issues.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error && issues.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading issues: {error}. Please try refreshing the page.</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ position: "relative" }}>
      <BacklogContainer >
        {selectedSprint && (
          <StartSprintDialog
            sprint={selectedSprint}
            projectKey={projectDetails?.key}
            onSprintStarted={handleSprintStarted}
            open={startSprintDialogOpen}
            onClose={handleCloseStartSprintDialog}
          />
        )}

        {editSprintDialogOpen && sprintToEdit && (
          <StartSprintDialog
            sprint={sprintToEdit}
            projectKey={projectDetails?.key}
            onSprintUpdated={handleSprintUpdated}
            open={editSprintDialogOpen}
            onClose={handleCloseEditSprintDialog}
            mode="edit"
          />
        )}

        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleToastClose} severity={toast.type} sx={{ width: "100%" }}>
            {toast.message}
          </Alert>
        </Snackbar>

        {/* Header */}
        <HeaderContainer>
          <Typography variant="h5" fontWeight="600">
            {projectDetails?.name || "Project"} Backlog
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            {/* Export Button with Dropdown */}
            <FilterButton
              variant="outlined"
              onClick={handleExportClick}
              startIcon={<ExportIcon />}
              disabled={loading}
            >
              Export
            </FilterButton>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportMenuOpen}
              onClose={handleExportClose}
            >
              <MenuItem onClick={() => handleExportMenuItemClick('csv')}>
                Export as CSV
              </MenuItem>
           
            </Menu>

            <CreateButton variant="contained" startIcon={<AddIcon />} onClick={goToCreateIssuePage}>
              Change Request
            </CreateButton>

            <CreateButton variant="contained" onClick={handleCreateSprint}>
              Create Sprint
            </CreateButton>
          </Box>
        </HeaderContainer>

        {/* Search and filters section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3, 
          gap: 2,
          flexWrap: 'wrap' 
        }}>
          {/* Search bar */}
          <TextField
            placeholder="Search issues"
            size="small"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={() => setSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ 
              width: '300px', 
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                height: '36px',
              }
            }}
          />

          {/* Type filter */}
          <FilterButton
            variant="outlined"
            onClick={handleTypeFilterClick}
            endIcon={<ArrowDropDownIcon />}
            size="small"
            startIcon={<FilterIcon fontSize="small" />}
          >
            {getFilterButtonText('type')}
          </FilterButton>
          <Menu
            anchorEl={typeFilterAnchorEl}
            open={typeFilterOpen}
            onClose={handleTypeFilterClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '200px',
              },
            }}
          >
            {['Epic', 'Story', 'Task', 'Bug'].map((type) => {
              const issueType = issueTypes.find(t => t.name === type)
              return (
                <MenuItem 
                  key={type}
                  onClick={() => handleTypeFilterToggle(type.toLowerCase())}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {issueType?.icon}
                    <Typography sx={{ ml: 1 }}>{type}</Typography>
                  </Box>
                  <Checkbox
                    checked={activeFilters[type.toLowerCase()]}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </MenuItem>
              )
            })}
          </Menu>

          {/* Status filter */}
          <FilterButton
            variant="outlined"
            onClick={handleStatusFilterClick}
            endIcon={<ArrowDropDownIcon />}
            size="small"
            startIcon={<FilterIcon fontSize="small" />}
          >
            {getFilterButtonText('status')}
          </FilterButton>
          <Menu
            anchorEl={statusFilterAnchorEl}
            open={statusFilterOpen}
            onClose={handleStatusFilterClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '200px',
              },
            }}
          >
            {statuses.map((status) => (
              <MenuItem 
                key={status.id}
                onClick={() => handleStatusFilterToggle(status.name)}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      backgroundColor: status.color,
                      mr: 1.5,
                    }}
                  />
                  <Typography>{status.name}</Typography>
                </Box>
                <Checkbox
                  checked={selectedStatuses.includes(status.name)}
                  size="small"
                  sx={{ p: 0 }}
                />
              </MenuItem>
            ))}
          </Menu>

          {/* Assignee filter */}
          <FilterButton
            variant="outlined"
            onClick={handleAssigneeFilterClick}
            endIcon={<ArrowDropDownIcon />}
            size="small"
            startIcon={<PersonIcon fontSize="small" />}
          >
            {getFilterButtonText('assignee')}
          </FilterButton>
          <Menu
            anchorEl={assigneeFilterAnchorEl}
            open={assigneeFilterOpen}
            onClose={handleAssigneeFilterClose}
            PaperProps={{
              style: {
                maxHeight: 400,
                width: '250px',
              },
            }}
          >
            <MenuItem onClick={() => handleAssigneeFilterToggle('Unassigned')}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '0.75rem',
                      bgcolor: '#DFE1E6',
                      color: '#172B4D',
                      mr: 1.5,
                    }}
                  >
                    {getInitials('Unassigned')}
                  </Avatar>
                  <Typography>Unassigned</Typography>
                </Box>
                <Checkbox
                  checked={selectedAssignees.includes('Unassigned')}
                  size="small"
                  sx={{ p: 0 }}
                />
              </Box>
            </MenuItem>
            
            {/* Team members */}
            {teamMembers.map((member) => (
              <MenuItem 
                key={member.id}
                onClick={() => handleAssigneeFilterToggle(member.name)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: '#0052CC',
                        color: '#FFFFFF',
                        mr: 1.5,
                      }}
                    >
                      {getInitials(member.name)}
                    </Avatar>
                    <Box>
                      <Typography>{member.name}</Typography>
                      {member.role && (
                        <Typography variant="caption" color="text.secondary">
                          {member.role}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Checkbox
                    checked={selectedAssignees.includes(member.name)}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </Box>
              </MenuItem>
            ))}
            
            {/* Additional assignees from issues */}
            {getAllAssignees()
              .filter(assignee => !teamMembers.some(member => member.name === assignee) && assignee !== 'Unassigned')
              .map((assignee) => (
                <MenuItem 
                  key={assignee}
                  onClick={() => handleAssigneeFilterToggle(assignee)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: '0.75rem',
                          bgcolor: '#0052CC',
                          color: '#FFFFFF',
                          mr: 1.5,
                        }}
                      >
                        {getInitials(assignee)}
                      </Avatar>
                      <Typography>{assignee}</Typography>
                    </Box>
                    <Checkbox
                      checked={selectedAssignees.includes(assignee)}
                      size="small"
                      sx={{ p: 0 }}
                    />
                  </Box>
                </MenuItem>
              ))}
          </Menu>

          {/* Priority filter */}
          <FilterButton
            variant="outlined"
            onClick={handlePriorityFilterClick}
            endIcon={<ArrowDropDownIcon />}
            size="small"
            startIcon={<FlagIcon fontSize="small" />}
          >
            {getFilterButtonText('priority')}
          </FilterButton>
          <Menu
            anchorEl={priorityFilterAnchorEl}
            open={priorityFilterOpen}
            onClose={handlePriorityFilterClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '200px',
              },
            }}
          >
            {priorities.slice(0, 5).map((priority) => (
              <MenuItem 
                key={priority.id}
                onClick={() => handlePriorityFilterToggle(priority.name)}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {priority.icon}
                  <Typography sx={{ ml: 1.5 }}>{priority.name}</Typography>
                </Box>
                <Checkbox
                  checked={selectedPriorities.includes(priority.name)}
                  size="small"
                  sx={{ p: 0 }}
                />
              </MenuItem>
            ))}
          </Menu>

          {/* Sprint filter */}
          <FilterButton
            variant="outlined"
            onClick={handleSprintFilterClick}
            endIcon={<ArrowDropDownIcon />}
            size="small"
            startIcon={<SprintIcon fontSize="small" />}
          >
            {getFilterButtonText('sprint')}
          </FilterButton>
          <Menu
            anchorEl={sprintFilterAnchorEl}
            open={sprintFilterOpen}
            onClose={handleSprintFilterClose}
            PaperProps={{
              style: {
                maxHeight: 400,
                width: '250px',
              },
            }}
          >
            {/* Backlog option */}
            <MenuItem onClick={() => handleSprintFilterToggle('Backlog')}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography>Backlog</Typography>
                <Checkbox
                  checked={selectedSprints.includes('Backlog')}
                  size="small"
                  sx={{ p: 0 }}
                />
              </Box>
            </MenuItem>
            
            {/* Sprint options */}
            {sprints.map((sprint) => (
              <MenuItem 
                key={sprint.id}
                onClick={() => handleSprintFilterToggle(sprint.name)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SprintIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} />
                    <Box>
                      <Typography>{sprint.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sprint.issues.length} issue(s)
                      </Typography>
                    </Box>
                  </Box>
                  <Checkbox
                    checked={selectedSprints.includes(sprint.name)}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Menu>

          {/* Clear all filters button */}
          {hasAnyFilter() && (
            <Button
              size="small"
              onClick={clearAllFilters}
              startIcon={<ClearIcon />}
              variant="outlined"
              sx={{ ml: 'auto' }}
            >
              Clear all
            </Button>
          )}
        </Box>

        {/* Active filters chips */}
        {hasAnyFilter() && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {/* Type filters */}
            {Object.entries(activeFilters).map(([type, isActive]) => {
              if (isActive) {
                return (
                  <Chip
                    key={type}
                    label={`Type: ${type.charAt(0).toUpperCase() + type.slice(1)}`}
                    size="small"
                    onDelete={() => handleTypeFilterToggle(type)}
                    sx={{ bgcolor: '#e3f2fd' }}
                  />
                )
              }
              return null
            })}

            {/* Status filters */}
            {selectedStatuses.map(status => (
              <Chip
                key={status}
                label={`Status: ${status}`}
                size="small"
                onDelete={() => handleStatusFilterToggle(status)}
                sx={{ bgcolor: '#e8f5e9' }}
              />
            ))}

            {/* Assignee filters */}
            {selectedAssignees.map(assignee => (
              <Chip
                key={assignee}
                label={`Assignee: ${assignee}`}
                size="small"
                onDelete={() => handleAssigneeFilterToggle(assignee)}
                sx={{ bgcolor: '#fff3e0' }}
              />
            ))}

            {/* Priority filters */}
            {selectedPriorities.map(priority => (
              <Chip
                key={priority}
                label={`Priority: ${priority}`}
                size="small"
                onDelete={() => handlePriorityFilterToggle(priority)}
                sx={{ bgcolor: '#fce4ec' }}
              />
            ))}

            {/* Sprint filters */}
            {selectedSprints.map(sprint => (
              <Chip
                key={sprint}
                label={`Sprint: ${sprint}`}
                size="small"
                onDelete={() => handleSprintFilterToggle(sprint)}
                sx={{ bgcolor: '#f3e5f5' }}
              />
            ))}

            {/* Search filter */}
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery('')}
                sx={{ bgcolor: '#e0f2f1' }}
              />
            )}
          </Box>
        )}

        {/* Export summary info */}
        <Box sx={{ 
          mb: 3, 
          p: 1.5, 
          bgcolor: '#f8f9fa', 
          borderRadius: 1,
          border: '1px solid #e9ecef'
        }}>
          <Typography variant="body2" color="text.secondary">
            Showing {getVisibleIssues().length} issue(s)
            {searchQuery && ` matching "${searchQuery}"`}
            {hasAnyFilter() && ' with filters applied'}
            {selectedSprints.length > 0 && ` in ${selectedSprints.length} sprint(s)`}
            {'. Export will include all visible issues.'}
          </Typography>
        </Box>

        {/* Main content */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <EnhancedAISprintSuggestion
              backlogIssues={backlogIssuesForAI}
              projectId={projectId}
              projectKey={projectDetails?.key}
              onApplySuggestion={handleApplyAISuggestion}
              sprints={sprints}
            />

            {/* Sprints sections */}
            {sprints
              .filter(sprint => selectedSprints.length === 0 || selectedSprints.includes(sprint.name))
              .map((sprint) => {
                const filteredSprintIssues = sprint.issues.filter(issue => {
                  // Search filter
                  const matchesSearch = !searchQuery.trim() || 
                    issue.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    issue.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (issue.assignee && issue.assignee.toLowerCase().includes(searchQuery.toLowerCase()))

                  // Type filter
                  const activeTypeFilters = Object.keys(activeFilters).filter(key => activeFilters[key])
                  let matchesTypeFilter = true
                  if (activeTypeFilters.length > 0) {
                    const issueTypeName = getIssueTypeName(issue.issueType || issue.type)
                    matchesTypeFilter = activeTypeFilters.includes(issueTypeName.toLowerCase())
                  }

                  // Status filter
                  let matchesStatusFilter = true
                  if (selectedStatuses.length > 0) {
                    const statusName = getStatusName(issue.status || '')
                    matchesStatusFilter = selectedStatuses.includes(statusName)
                  }

                  // Assignee filter
                  let matchesAssigneeFilter = true
                  if (selectedAssignees.length > 0) {
                    const assigneeName = issue.assigneeName || issue.assignee || 'Unassigned'
                    matchesAssigneeFilter = selectedAssignees.includes(assigneeName)
                  }

                  // Priority filter
                  let matchesPriorityFilter = true
                  if (selectedPriorities.length > 0) {
                    const priorityName = getPriorityName(issue.priority || '')
                    matchesPriorityFilter = selectedPriorities.includes(priorityName)
                  }

                  return matchesSearch && matchesTypeFilter && matchesStatusFilter && matchesAssigneeFilter && matchesPriorityFilter
                })

                return (
                  <SprintSection
                    key={sprint.id}
                    sprint={{
                      ...sprint,
                      issues: filteredSprintIssues 
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropOnSprint(e, sprint.id)}
                    onToggleExpanded={() => toggleSprintExpanded(sprint.id)}
                    onStartSprint={() => handleOpenStartSprintDialog(sprint)}
                    onEditSprint={handleEditSprint}
                    onSprintDeleted={handleSprintDeleted}
                    navigate={navigate}
                    projectId={projectId}
                    handleDragStart={handleDragStart}
                    issueTypes={issueTypes}
                    statuses={statuses}
                    priorities={priorities}
                    getInitials={getInitials}
                    onIssueClick={handleIssueClick}
                    onAssigneeChange={handleAssigneeUpdate}
                    onStatusChange={handleStatusUpdate}
                    onPriorityChange={handlePriorityUpdate}
                    onParentChange={handleParentChange}
                    onMenuAction={handleMenuAction}
                    onMove={handleMoveIssue}
                    projectKey={projectDetails?.key}
                    getIssueDependencyInfo={getIssueDependencyInfo}
                  />
                )
              })}

            {/* Backlog section - Show only if selected or no sprint filter */}
            {(selectedSprints.length === 0 || selectedSprints.includes("Backlog")) && (
              <Paper
                variant="outlined"
                sx={{
                  overflow: "visible",
                  mb: 2,
                  borderRadius: "3px",
                  boxShadow: "0 1px 1px rgba(9, 30, 66, 0.25)",
                }}
                onDragOver={handleDragOver}
                onDrop={handleDropOnBacklog}
              >
                <Box
                  sx={{
                    borderBottom: backlogExpanded ? "1px solid #DFE1E6" : "none",
                    bgcolor: "#F4F5F7",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#EBECF0" },
                  }}
                  onClick={toggleBacklogExpanded}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton size="small" sx={{ p: 0.5, mr: 1 }}>
                        <ExpandMoreIcon
                          fontSize="small"
                          sx={{
                            transform: backlogExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      </IconButton>
                      <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                        Backlog
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      (
                      {
                        filteredIssues.filter(
                          (issue) =>
                            !sprints.some((sprint) => sprint.issues.some((sprintIssue) => sprintIssue.id === issue.id)),
                        ).length
                      }{" "}
                      issues)
                    </Typography>
                  </Box>
                </Box>

                <Collapse in={backlogExpanded}>
                  <Box sx={{ bgcolor: "white" }}>{renderBacklogContent()}</Box>
                </Collapse>
              </Paper>
            )}

            {/* Message when no sprints/backlog match filter */}
            {selectedSprints.length > 0 && 
             sprints.filter(s => selectedSprints.includes(s.name)).length === 0 && 
             !selectedSprints.includes("Backlog") && (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center', 
                border: '1px dashed #ddd',
                borderRadius: 1,
                bgcolor: '#f9f9f9'
              }}>
                <Typography color="text.secondary">
                  No sprints match the selected filter. Try different sprint selections or clear filters.
                </Typography>
                <Button 
                  onClick={clearAllFilters} 
                  variant="outlined" 
                  size="small"
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </BacklogContainer>

      {/* Task Details Panel */}
      {isPanelOpen && selectedIssue && (
        <>
        {/* Backdrop/Overlay */}
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1299,
            backdropFilter: "blur(2px)",
          }}
          onClick={handlePanelClose}
        />
        <TaskDetailsPanel
          issue={selectedIssue}
          onClose={handlePanelClose}
          onUpdate={handleIssueUpdate}
          issueTypes={issueTypes}
          statuses={statuses}
          priorities={priorities}
          getInitials={getInitials}
        />
          </>
      )}
    </Box>
  )
}

export default BacklogPage